/**
 * /zh/product-strategy — 产品策略中文 SEO 页。与 ProductStrategy.tsx
 * 同构，原生中文，hreflang 双向。
 */
import { MarketingShell } from './MarketingShell';

export function ProductStrategyZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/product-strategy"
      documentTitle="产品策略是什么？框架与示例 · Draftlize"
      documentDescription="产品策略是连接愿景与路线图的一组赌注：你为谁、解决什么问题、为什么是现在、以及不做什么。一份能直接填的框架与示例。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 产品策略</span>
          <h1 className="serif" style={{ fontSize: 'clamp(38px, 4.8vw, 58px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            产品策略是什么？<br/>
            <em>一组你拿得出手去辩护的赌注。</em>
          </h1>
          <p className="lede">
            <em>产品策略</em>是愿景（「我们要去哪」）和路线图（「我们要做什么」）之间的连接组织。它是一组刻意的赌注，解释<em>为什么是这个、给谁、为什么是现在</em>——以及同样重要的，你选择不做什么。没有它，路线图就是一张配了日历的功能清单。这页把产品策略讲清楚，给你一份真能填进去的框架，走一遍示例，然后诚实地谈那个没人为之做准备的失败：一份没人能把它追溯到它本应驱动的工作上的策略。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">那么，<em>产品策略到底是什么？</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            产品策略是关于「一个产品如何取胜」的一小组选择：哪个客户、哪个问题、为什么你的打法胜过替代方案、以及你会刻意忽略什么来做到。它不是愿景（那是终点），也不是路线图（那是路线）。策略是中间那段推理——它一旦缺席，每场优先级争论都会显得任意，因为没人对「在<em>什么</em>上取胜？」有共识。
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: 0 }}>
            真正的策略让人不舒服，因为它要承诺。「我们服务所有人」不是策略；「我们先拿下独立 PM，企业采购流程在不得不面对之前先不管」才是。检验一份<em>产品管理策略</em>的标准是：它有没有告诉你该对什么说不。如果它只会往上加东西，那它就是一张披着策略外衣的愿望清单。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">框架</span>
            <h2 className="serif">一份真策略<em>到底包含什么。</em></h2>
          </div>
          <p>六个部分。无论你写在一页纸上（一份「策略模板」）还是搭在 Draftlize 里，骨架都一样——每一项填诚实了，下游的路线图几乎会自己排好优先级。</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: '目标客户', hint: '具体是谁', body: '你要先拿下的那个具体人群——窄到你能点出其中三个的名字。「所有人」是策略里最昂贵的错误。滩头阵地越窄，每一个下游决策就越锋利。' },
            { k: '问题与洞察', hint: '为什么要紧', body: '你为那个客户解决的问题，以及你对它有、别人没有的那个不显而易见的洞察。洞察是竞品没法靠读你的路线图抄走的部分——它是你的打法为什么「对」、而不只是「不一样」的原因。' },
            { k: '为什么是现在', hint: '时机的赌注', body: '世界里发生了什么变化，让这成为那个时刻——一项新能力、一种行为转变、一项骤降的成本。每份强策略都骑在一个「为什么是现在」上；没有它，你要么太早（没市场），要么太晚（没空间）。' },
            { k: '差异化', hint: '为什么你赢', body: '你击败现状和明显替代方案的理由——最好还能说清这个优势为什么会复利累积、而不是被竞争抹平。如果你说不出来，你就是在单押执行，那是一个脆弱的赌注。' },
            { k: '非目标', hint: '不做什么', body: '你此刻刻意谢绝的那些诱人的客户、功能和市场。这是策略真正值钱的地方——说不，才让那些「做」是值得投入的。一份没有非目标的策略不算策略。' },
            { k: '怎么衡量它', hint: '成没成', body: '在滞后的营收数字之前，就告诉你策略在不在奏效的那几个信号——激活、目标人群里的留存、对那个点名的替代方案的胜率。提前定好，免得事后给自己找补。' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <span className="serif" style={{ fontSize: 18, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.3 }}>{f.k}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.75, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">策略<em>站在哪。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">愿景 → 策略 → 路线图</h3>
            <p>愿景是终点（「产品决策永不漂移」）。策略是你将如何到达并取胜（「占住 AI 原生 PM 工作底下的决策层；从独立 PM 起步」）。<a href="/zh/what-is-a-product-roadmap" style={{ color: 'var(--amber)' }}>路线图</a> 是你为执行它接下来要做的东西。每一层约束下一层——跳过策略，路线图就飘了。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">策略喂给优先级</h3>
            <p>清晰的策略让 <a href="/zh/prioritization-frameworks" style={{ color: 'var(--amber)' }}>优先级</a> 几乎变成机械活：服务于那个点名客户和「为什么是现在」的条目上升，服务于某个非目标的条目下降。当排优先级感觉像吵架，真正的问题通常在上游——一份从未承诺的策略。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">发现去检验赌注</h3>
            <p>策略是一组假设，而 <a href="/zh/product-discovery" style={{ color: 'var(--amber)' }}>产品发现</a> 是你在押上路线图之前给它们做压力测试的方式。洞察和「为什么是现在」都是假说；发现就是你弄清哪些经得起真实客户撞击的地方。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">策略是一组赌注。<em>赌注会变。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            常见的失败不是策略差——而是一份好策略没人能把它追溯到工作上。策略住在一份 deck 里，路线图和 spec 住在别处，赌注一变，没有任何东西把两头连起来。Draftlize 把这道缝合上。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每个赌注都是可寻址卡片</h3>
            <p>目标客户、那个洞察、「为什么是现在」、每一个非目标——在 Draftlize 里它们是结构化卡片，而不是路线图永远不引用的某页幻灯片上的项目符号。可按 ID 引用，于是一个路线图条目或 spec 能指向它所服务的那个确切的策略赌注。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">赌注一变，依赖它的标 stale</h3>
            <p>你修订了目标人群，或某个「为什么是现在」的假设被证伪。所有连到那个赌注的路线图条目、PRD、spec 自动标记 stale——于是策略的转向会传播到工作上，而不是让一份 deck 和一份路线图安静地各说各话整整一个季度。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">你的 agent 基于「活」策略来构建</h3>
            <p>当 Claude Code 或 Cursor 经 MCP 起草下一份 spec，它读到的是当前的策略赌注——而不是上次 offsite 的那一版。策略不再是被引用完就忘的文档，而成为每个下游决策真正会去比对的 context。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          多数策略失败，不是因为它错了，而是因为没有任何东西把赌注连到工作上——于是赌注一变，工作没跟着变。
        </blockquote>
        <cite>把赌注写下来，让 substrate 替你保持路线图对它的忠实。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '产品策略和路线图有什么区别？', a: '策略是推理——你服务谁、你的打法为什么赢、你不做什么；路线图是你为执行那套推理而要做的东西的计划。策略解释为什么，路线图展示做什么、大致何时。背后没有策略的路线图，就是一张带日期的功能清单。' },
            { q: '产品策略和愿景是一回事吗？', a: '不是。愿景是终点，常常在数年之外、带着理想色彩；策略是你打算如何到达、如何战胜替代方案，带着真实的选择与取舍。愿景鼓舞人，策略做承诺。' },
            { q: '产品策略归谁负责？', a: '产品负责人与创始人或高管团队一起定，产品经理把它翻译成路线图。但它取材于工程、设计、销售和市场现实——一份闭门造出来的策略，很少能在要去执行它的人面前活下来。' },
            { q: '产品策略应该多久变一次？', a: '赌注应当稳到能据以规划——通常每季度、或当某个核心假设被证伪时回看，而不是每个 sprint。纪律在于：当一个赌注失效时刻意地改它，并确保路线图跟着动，而不是让两者安静地各自漂走。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">让你的策略与路线图同步<em>——$5 免费起步。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>只为实际用量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            把你的策略赌注作为卡片写进 Draftlize，把路线图和 spec 连到它们，让一个变化的赌注去标记所有下游——这样你的策略和它驱动的工作，永远不会安静地各自漂走。
          </p>
          <a className="btn-primary" href="/">$5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
